// Local helper to handle currency conversions across USD, EUR, AED, etc.
// Base rates in fxRates are stored relative to USD (1 USD = rate units of target currency)

// Currency-specific FX buffer rates:
// - USD: 0% buffer (base currency)
// - AED: 0% buffer (pegged directly to USD at 3.6725)
// - EUR: 2% buffer (+2% exchange volatility buffer)
export const DEFAULT_FX_BUFFERS = {
  USD: 0,
  AED: 0,
  EUR: 0.02,
};

/**
 * Returns the FX multiplier for a currency including its configured volatility buffer.
 * e.g. USD -> 1.0, AED -> 1.0, EUR -> 1.02
 */
export const getFxBufferMultiplier = (currency) => {
  const curr = (currency || 'USD').toUpperCase();
  const bufferPercent = DEFAULT_FX_BUFFERS[curr] !== undefined ? DEFAULT_FX_BUFFERS[curr] : 0;
  return 1 + bufferPercent;
};

export const convertCurrency = (amount, sourceCurrency = 'USD', targetCurrency = 'USD', fxRates) => {
  if (amount == null || isNaN(amount)) {
    return { amount: 0, currency: targetCurrency || 'USD' };
  }

  const source = (sourceCurrency || 'USD').toUpperCase();
  const target = (targetCurrency || 'USD').toUpperCase();

  if (source === target) {
    return { amount: Number(amount), currency: target };
  }

  const defaultRates = { AED: 3.6725, EUR: 0.92 };
  const rates = (fxRates && typeof fxRates === 'object' && Object.keys(fxRates).length > 0)
    ? { ...defaultRates, ...fxRates }
    : defaultRates;

  // Step 1: Convert source amount to base USD
  let amountUSD = Number(amount);
  if (source !== 'USD') {
    const sourceRate = rates[source] || defaultRates[source];
    if (sourceRate && sourceRate > 0) {
      // Remove currency buffer if it was applied when converting to source
      const sourceBuffer = getFxBufferMultiplier(source);
      amountUSD = amount / (sourceRate * sourceBuffer);
    }
  }

  // Step 2: Convert base USD to target currency
  if (target === 'USD') {
    const finalAmount = Number(amountUSD.toFixed(2));
    return { amount: finalAmount, currency: 'USD' };
  }

  const targetRate = rates[target];
  if (!targetRate || targetRate <= 0) {
    const finalAmount = Number(amountUSD.toFixed(2));
    return { amount: finalAmount, currency: 'USD' };
  }

  // Apply currency-specific FX buffer for conversion to target currency (e.g. 2% for EUR, 0% for AED)
  const targetBuffer = getFxBufferMultiplier(target);
  const rawConverted = amountUSD * targetRate * targetBuffer;
  const finalAmount = Number(rawConverted.toFixed(2));

  return { amount: finalAmount, currency: target };
};

export const convertUSDToSelected = (amountUSD, selectedCurrency, fxRates, sourceCurrency = 'USD') => {
  return convertCurrency(amountUSD, sourceCurrency, selectedCurrency, fxRates);
};

// Backwards compatibility layer for legacy components
export const convertAEDToSelected = (amountAED, selectedCurrency, fxRates) => {
  return convertCurrency(amountAED, 'AED', selectedCurrency, fxRates);
};

export const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'AED': return 'AED ';
    default: return '';
  }
};

